/**
 * Client Activation Banner
 *
 * Shown in the user's dashboard — a system-driven activation funnel
 * that tracks their path from signup to financing-ready.
 *
 * Rules:
 * - Auto-computed from real platform data (no manual ticking)
 * - Role-aware (SME / Project / Corporate / FI)
 * - Disappears when 100% complete (replaced by celebration state)
 * - Fire-and-forget tracking for events (impact_reviewed, report_generated)
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ChevronDown, ChevronUp, Zap, ArrowRight } from "lucide-react";
import client from "@/api/client";

const STATUS_CONFIG = {
  not_started: { label: "Not Started",  color: "text-slate-400",  bg: "bg-slate-100" },
  in_progress:  { label: "In Progress", color: "text-amber-600",  bg: "bg-amber-50"  },
  submitted:    { label: "Submitted",   color: "text-blue-600",   bg: "bg-blue-50"   },
  complete:     { label: "Ready",       color: "text-emerald-600",bg: "bg-emerald-50"},
};

export default function ActivationBanner() {
  const navigate           = useNavigate();
  const [data,    setData] = useState(null);
  const [open,    setOpen] = useState(true);
  const [loading, setLoad] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await client.get("/activation/my-progress");
      setData(res.data);
    } catch {}
    setLoad(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return null;
  if (data.pct === 100 && !open) return null;   // fully complete + dismissed

  const sc = STATUS_CONFIG[data.status] || STATUS_CONFIG.in_progress;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mb-5"
      data-testid="activation-banner">

      {/* Header bar */}
      <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-[#0F172A] to-[#1E293B] cursor-pointer"
        onClick={() => setOpen(o => !o)}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-teal-500/20 flex items-center justify-center flex-shrink-0">
            <Zap size={14} className="text-teal-400" />
          </div>
          <div>
            <p className="text-xs font-bold text-white">Client Activation Progress</p>
            <p className="text-[10px] text-slate-400">Complete these steps to unlock financing readiness</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Progress bar */}
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-28 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-teal-400 rounded-full transition-all duration-500"
                style={{ width: `${data.pct}%` }} />
            </div>
            <span className="text-[10px] font-bold text-teal-400">{data.pct}%</span>
          </div>
          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${sc.bg} ${sc.color}`}>
            {sc.label}
          </span>
          {open ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
        </div>
      </div>

      {/* Body */}
      {open && (
        <div className="px-5 py-4">
          {/* Mobile progress bar */}
          <div className="sm:hidden mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-slate-500">{data.completed} of {data.total} complete</span>
              <span className="text-[10px] font-bold text-teal-600">{data.pct}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-teal-500 rounded-full transition-all duration-500"
                style={{ width: `${data.pct}%` }} />
            </div>
          </div>

          {/* Step list */}
          <div className="space-y-1.5" data-testid="activation-steps">
            {data.steps.map((step, i) => (
              <div key={step.key}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all
                  ${step.done ? "bg-emerald-50/60" : "bg-slate-50 hover:bg-slate-100"}`}
                data-testid={`activation-step-${step.key}`}>

                {/* Indicator */}
                {step.done
                  ? <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                  : <Circle size={16} className={`flex-shrink-0 ${i === data.steps.findIndex(s => !s.done) ? "text-teal-500" : "text-slate-300"}`} />
                }

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-xs font-semibold ${step.done ? "text-emerald-700" : "text-[#0F172A]"}`}>
                      {step.title}
                    </p>
                    {step.done && (
                      <span className="text-[9px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full">Done</span>
                    )}
                    {!step.done && i === data.steps.findIndex(s => !s.done) && (
                      <span className="text-[9px] font-bold text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded-full">Next</span>
                    )}
                  </div>
                  {!step.done && (
                    <p className="text-[10px] text-slate-400 mt-0.5 truncate">{step.desc}</p>
                  )}
                </div>

                {/* CTA */}
                {!step.done && step.cta && i === data.steps.findIndex(s => !s.done) && (
                  <button
                    onClick={e => { e.stopPropagation(); navigate(step.cta.path); }}
                    className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white text-[10px] font-bold rounded-lg hover:bg-teal-700 transition-colors"
                    data-testid={`activation-cta-${step.key}`}>
                    {step.cta.label}
                    <ArrowRight size={10} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Guidance + encouragement */}
          {data.pct === 100 ? (
            <div className="mt-3 flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl">
              <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" />
              <p className="text-xs font-bold text-emerald-800">You are financing-ready. Your profile is fully activated.</p>
              <button onClick={() => setOpen(false)}
                className="ml-auto text-[10px] text-emerald-600 hover:underline flex-shrink-0">Dismiss</button>
            </div>
          ) : (
            <div className="mt-3 px-3 py-2 bg-slate-50 rounded-xl">
              <p className="text-[10px] text-slate-600">{data.guidance}</p>
              {data.encouragement && (
                <p className="text-[9px] text-slate-400 mt-0.5 italic">{data.encouragement}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
